import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const UGCKaufen = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/?lang=de");
    }, 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Was kostet ein UGC Video?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "UGC Videos bei Fuel72 starten ab 49€ pro Video. Je nach Paket und Anzahl der Videos gibt es Rabatte. Ein Starter-Paket mit 3 Videos kostet 147€, ein Pro-Paket mit 5 Videos 225€."
        }
      },
      {
        "@type": "Question",
        name: "Wie lange dauert die UGC Video Produktion?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Mit unserer KI-gestützten Produktion erhalten Sie Ihre UGC Videos innerhalb von 72 Stunden. Kein wochenlanges Warten auf Creator oder Drehtage."
        }
      },
      {
        "@type": "Question",
        name: "Kann ich UGC Videos ohne Creator kaufen?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Ja! Mit Fuel72 erstellen Sie authentische UGC Videos komplett mit KI – ohne echte Creator buchen zu müssen. Gleiche Qualität, schnellere Lieferung, niedrigere Kosten."
        }
      },
      {
        "@type": "Question",
        name: "Für welche Plattformen eignen sich die UGC Videos?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Unsere UGC Videos sind optimiert für TikTok, Instagram Reels, Facebook Ads, YouTube Shorts und alle anderen Social Media Plattformen."
        }
      }
    ]
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "UGC Video Paket",
    description: "Professionelle UGC Videos kaufen - KI-generiert in 72 Stunden",
    brand: {
      "@type": "Brand",
      name: "Fuel72"
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: "49",
      highPrice: "225",
      offerCount: "3"
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "127"
    }
  };

  return (
    <>
      <Helmet>
        <html lang="de" />
        <title>UGC kaufen | UGC Videos kaufen ab 49€ | Fuel72</title>
        <meta
          name="description"
          content="UGC kaufen bei Fuel72 ✓ UGC Videos kaufen ab 49€ ✓ UGC Content kaufen für TikTok, Instagram & Meta Ads ✓ Lieferung in 72h ✓ Ohne Creator-Suche"
        />
        <meta
          name="keywords"
          content="ugc kaufen, ugc video kaufen, ugc videos kaufen, ugc content kaufen, ugc paket kaufen, ugc bestellen, ugc online kaufen, ugc videos bestellen, ugc content bestellen, ugc für ads kaufen"
        />
        <link rel="canonical" href="https://www.fuel72.com/ugc-kaufen" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="UGC kaufen | UGC Videos kaufen ab 49€ | Fuel72" />
        <meta property="og:description" content="UGC Videos kaufen ab 49€. Professioneller UGC Content für TikTok, Instagram Reels & Meta Ads. Lieferung in 72 Stunden." />
        <meta property="og:url" content="https://www.fuel72.com/ugc-kaufen" />
        <meta property="og:image" content="https://www.fuel72.com/fuel72-logo.png" />
        <meta property="og:locale" content="de_DE" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="UGC kaufen | UGC Videos kaufen ab 49€" />
        <meta name="twitter:description" content="Professionelle UGC Videos kaufen. KI-generiert in 72h. Für TikTok, Reels & Meta Ads." />
        
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/ugc-kaufen" />
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/buy-ugc" />
        <link rel="alternate" hrefLang="x-default" href="https://www.fuel72.com/buy-ugc" />
        
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      <main className="min-h-screen bg-background text-foreground p-8">
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-6">
            UGC kaufen – Professionelle UGC Videos ab 49€
          </h1>
          
          <p className="text-xl text-muted-foreground mb-8">
            UGC Videos kaufen war noch nie so einfach. Mit Fuel72 erhalten Sie hochwertigen 
            UGC Content für Ihre Social Media Kampagnen – ohne Creator-Suche, ohne Wartezeiten.
          </p>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Warum UGC Videos kaufen?</h2>
            <p className="mb-4">
              User Generated Content (UGC) ist der effektivste Content-Typ für Social Media Ads. 
              UGC Videos erzielen bis zu 4x höhere Engagement-Raten und 50% niedrigere CPAs 
              als traditionelle Werbung.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>UGC kaufen für TikTok</strong> – Authentische Videos im TikTok-Stil</li>
              <li><strong>UGC kaufen für Instagram Reels</strong> – Optimiert für maximale Reichweite</li>
              <li><strong>UGC kaufen für Meta Ads</strong> – Conversion-optimierte Creatives</li>
              <li><strong>UGC Content kaufen</strong> – Testimonials, Unboxings, Reviews</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">UGC Pakete kaufen</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">Starter</h3>
                <p className="text-3xl font-bold mb-4">147€</p>
                <p className="text-muted-foreground">3 UGC Videos</p>
                <p className="text-sm text-muted-foreground">49€ pro Video</p>
              </div>
              <div className="border border-primary rounded-lg p-6 relative">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 text-sm rounded-full">Beliebt</span>
                <h3 className="text-xl font-semibold mb-2">Pro</h3>
                <p className="text-3xl font-bold mb-4">225€</p>
                <p className="text-muted-foreground">5 UGC Videos</p>
                <p className="text-sm text-muted-foreground">45€ pro Video</p>
              </div>
              <div className="border border-border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">Scale</h3>
                <p className="text-3xl font-bold mb-4">390€</p>
                <p className="text-muted-foreground">10 UGC Videos</p>
                <p className="text-sm text-muted-foreground">39€ pro Video</p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">UGC kaufen – So funktioniert's</h2>
            <ol className="list-decimal pl-6 space-y-3">
              <li><strong>Paket wählen</strong> – Wählen Sie Ihr UGC Paket</li>
              <li><strong>Briefing ausfüllen</strong> – Beschreiben Sie Ihr Produkt und Ihre Ziele</li>
              <li><strong>Videos erhalten</strong> – Ihre UGC Videos in 72 Stunden</li>
              <li><strong>Kampagne starten</strong> – Direkt einsatzbereit für TikTok, Instagram & Meta</li>
            </ol>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Häufige Fragen zum UGC kaufen</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Was kostet ein UGC Video?</h3>
                <p className="text-muted-foreground">UGC Videos bei Fuel72 starten ab 49€ pro Video. Mit größeren Paketen sparen Sie bis zu 20%.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Wie schnell erhalte ich meine UGC Videos?</h3>
                <p className="text-muted-foreground">Dank KI-Produktion liefern wir Ihre UGC Videos innerhalb von 72 Stunden.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Kann ich UGC Videos ohne Creator kaufen?</h3>
                <p className="text-muted-foreground">Ja! Unsere KI-generierten UGC Videos benötigen keine echten Creator – gleiche Qualität, schnellere Lieferung.</p>
              </div>
            </div>
          </section>

          <p className="text-center text-muted-foreground">
            Weiterleitung zu Fuel72.com...
          </p>
        </article>
      </main>
    </>
  );
};

export default UGCKaufen;
